/**
 * Onboarding Progress Component
 *
 * Left sidebar with progress markers for desktop view.
 * Shows horizontal dots on mobile.
 *
 * @module components/onboarding/OnboardingProgress
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

export interface OnboardingProgressProps {
  /** Current active step */
  currentStep: number;
  /** Set of completed step numbers */
  completedSteps: Set<number>;
  /** Steps configuration */
  steps: Array<{
    number: number;
    title: string;
    description: string;
  }>;
  /** Callback when step is clicked */
  onStepClick?: (step: number) => void;
  /** Check if step is accessible */
  isStepAccessible?: (step: number) => boolean;
}

// =============================================================================
// Component
// =============================================================================

export function OnboardingProgress({
  currentStep,
  completedSteps,
  steps,
  onStepClick,
  isStepAccessible,
}: OnboardingProgressProps) {
  const currentStepData = steps.find((s) => s.number === currentStep);

  return (
    <>
      {/* Desktop: Vertical progress on left side (sticky) */}
      <div className="hidden lg:flex flex-col w-64 border-r border-gray-200 bg-gray-50 p-8 sticky top-0 h-screen overflow-y-auto">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-brand-500 overflow-hidden">
            <svg className="w-9 h-9" viewBox="0 0 32 32" fill="none">
              <path
                d="M7 10L13 16L7 22"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
              <path
                d="M13 10L19 16L13 22"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.75"
              />
              <path
                d="M19 10L25 16L19 22"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className="font-display font-semibold text-gray-900">
            Hummingbird
          </span>
        </div>

        <nav className="flex-1">
          <ol className="space-y-4">
            {steps.map((step, index) => {
              const isCompleted = completedSteps.has(step.number);
              const isCurrent = currentStep === step.number;
              const isAccessible = isStepAccessible?.(step.number) ?? true;
              const isLast = index === steps.length - 1;

              return (
                <li key={step.number} className="relative">
                  {/* Connector line */}
                  {!isLast && (
                    <div
                      className={cn(
                        'absolute left-4 top-[2.75rem] w-0.5 h-8 -translate-x-1/2',
                        isCompleted ? 'bg-brand-500' : 'bg-gray-200'
                      )}
                    />
                  )}

                  <button
                    onClick={() => isAccessible && onStepClick?.(step.number)}
                    disabled={!isAccessible}
                    className={cn(
                      'flex items-start gap-3 w-full text-left p-2 -ml-2 rounded-lg transition-colors',
                      isAccessible && !isCurrent && 'hover:bg-gray-100',
                      isCurrent && 'bg-white shadow-sm',
                      !isAccessible && 'cursor-not-allowed opacity-60'
                    )}
                  >
                    {/* Step indicator */}
                    <div
                      className={cn(
                        'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                        isCompleted && 'bg-brand-500 text-white',
                        isCurrent &&
                          !isCompleted &&
                          'bg-brand-500 text-white ring-4 ring-brand-100',
                        !isCompleted && !isCurrent && 'bg-gray-200 text-gray-500'
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        step.number
                      )}
                    </div>

                    {/* Step content */}
                    <div className="min-w-0 pt-0.5">
                      <p
                        className={cn(
                          'text-sm font-medium',
                          isCurrent || isCompleted
                            ? 'text-gray-900'
                            : 'text-gray-500'
                        )}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {step.description}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {/* Mobile: Full-width sticky header */}
      <div className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-10 w-full">
        {/* Single row: Logo left, step info right */}
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-brand-500 overflow-hidden flex-shrink-0">
              <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                <path
                  d="M7 10L13 16L7 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.5"
                />
                <path
                  d="M13 10L19 16L13 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.75"
                />
                <path
                  d="M19 10L25 16L19 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="font-display font-semibold text-gray-900">
              Hummingbird
            </span>
          </div>

          {/* Step info */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900">
              {currentStepData?.title}
            </span>
            <span className="text-sm text-gray-400">
              {currentStep} of {steps.length}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-brand-500 transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </>
  );
}
